import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { Reveal } from "@/components/common/Reveal";
import { AnimatedHeading } from "@/components/common/AnimatedHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { siteConfig } from "@/config/site.config";

/** Where the CTA originated — appended to the contact link so leads are tagged. */
export interface CtaOrigin {
  type: "service" | "solution" | "industry";
  slug: string;
  label: string;
}

function contactHref(origin?: CtaOrigin): string {
  if (!origin) return siteConfig.primaryCta.path;
  const params = new URLSearchParams({
    src_type: origin.type,
    src_slug: origin.slug,
    src_label: origin.label,
  });
  return `${siteConfig.primaryCta.path}?${params.toString()}`;
}

/** Final conversion block used at the bottom of every detail page. */
export function DetailCta({
  note,
  origin,
}: {
  note?: string;
  origin?: CtaOrigin;
}) {
  const href = contactHref(origin);
  return (
    <Section spacing="loose">
      <Container>
        <div className="relative overflow-hidden rounded-[2rem] border border-border bg-primary px-6 py-16 text-center text-primary-foreground sm:px-12 sm:py-20">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-20 top-0 h-72 w-72 rounded-full bg-accent/30 blur-[90px]" />
            <div className="absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-white/10 blur-[90px]" />
            <div className="absolute inset-0 bg-grid-light opacity-[0.3]" />
          </div>
          <div className="relative">
            <AnimatedHeading
              as="h2"
              lines={["Ready to build", "Something Great Together?"]}
              className="mx-auto max-w-2xl font-heading text-3xl font-extrabold leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mx-auto mt-5 max-w-xl text-lg text-white/80">
                {note ??
                  "Tell us where you are today and we'll map a clear, no-obligation path to launch."}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <CtaButton
                  to={href}
                  size="lg"
                  variant="secondary"
                  withArrow
                  magnetic
                  data-testid="detail-primary-cta"
                >
                  Book a Free Consultation
                </CtaButton>
                <CtaButton
                  to={href}
                  size="lg"
                  variant="ghost"
                  className="text-white hover:bg-white/10"
                  data-testid="detail-proposal-cta"
                >
                  Request a Proposal
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </Section>
  );
}
